import { BaseCollection } from '../BaseCollection'
import adminApiClient from '../../api/admin-api-client'
import { User } from './User'
import { EventBus } from '../../util/EventBus'

/**
 * Class UserCollection
 *
 * @extends BaseCollection
 */

export class UserCollection extends BaseCollection {
  constructor(props) {
    super(props)
    this.showImplicitUsers = false

    this.collectionKey = 'userDbId'
  }

  /**
   * /api/Users/querybycompany
   *
   * @param {object}
   * @returns {User[]} array of users
   *
   * @override
   */
  async fetch({ companyId, subdivisionId }) {
    this.isLoading = true

    try {
      /**
       * request params are setup in setRequestOpts
       * append implicit filter here
       */
      let implicitFilter = this.showImplicitUsers ? '' : 'implicit=false'

      implicitFilter = !this.showImplicitUsers && !!this.filter ? ',' + implicitFilter : implicitFilter

      let cleanedFilter = this.filter || ''
      const licenceMatch = cleanedFilter.match(/\(.*Appid.*\)/g)

      if (cleanedFilter && licenceMatch) {
        const storeFilterString = cleanedFilter.split(licenceMatch[0])
        let newLicenceString = licenceMatch[0]
        newLicenceString = newLicenceString.replaceAll('(', '').replaceAll(')', '')
        newLicenceString = newLicenceString.replace(/\|/g, ',')
        cleanedFilter = storeFilterString[0] + newLicenceString + storeFilterString[1]
      }

      let requestParams = {
        CompanyId: companyId,
        SubdivisionId: subdivisionId,
        Page: this.page,
        PageSize: this.pageSize,
        OrderBy: this.orderBy,
        Filter: cleanedFilter + implicitFilter,
      }
      const userResponse = await adminApiClient.getCompanyUsers(requestParams, subdivisionId)

      this.count = userResponse.data.count
      this.items = userResponse.data.data.map((u) => new User(u))
      this.isLoading = false
      if (this.filter) {
        EventBus.$emit('restoreFocusEvent')
      }

      return this.items
    } catch (ex) {
      this.isLoading = false
      throw new Error(ex.message)
    }
  }
  /**
   *
   * @param {*} companyId
   * @param {*} page
   * @param {*} pageSize
   * @param {*} orderBy
   * @param {*} filter
   * @returns
   */
  async legacyFetch(companyId, page, pageSize, orderBy, filter) {
    this.isLoading = true
    try {
      const userResponse = await adminApiClient.getCompanyUsers({
        CompanyId: companyId,
        Page: page,
        PageSize: pageSize,
        OrderBy: orderBy,
        Filter: filter,
      })

      this.count = userResponse.data.count
      this.items = userResponse.data.data.map((u) => new User(u))
      this.isLoading = false

      return this.items
    } catch (ex) {
      this.isLoading = false
      throw new Error(ex.message)
    }
  }

  async fetchById(userDbId) {
    this.isLoading = true
    try {
      const userResponse = await adminApiClient.getUser(userDbId)
      const entityContactsResponse = await adminApiClient.getUsersEntityContacts(userDbId)
      const entityAddressesResponse = await adminApiClient.getUsersEntityAddresses(userDbId)

      // Map entity contacts to their respective fields
      const entityContact = {}
      entityContactsResponse.data.forEach((contact) => {
        switch (contact.contactTypeId) {
          case 1:
            entityContact.phone = contact.contact
            break
          case 2:
            entityContact.commercialPhone = contact.contact
            break
          case 3:
            entityContact.email = contact.contact
            break
          case 4:
            entityContact.invoicingEmail = contact.contact
            break
          case 5:
            entityContact.trainingEmail = contact.contact
            break
        }
      })

      // Map entity addresses
      const entityAddress = {}
      // Add address type mapping
      const addressTypeIdToString = {
        1: 'primaryAddress',
        2: 'secondaryAddress',
        3: 'invoicingAddress',
      }

      entityAddressesResponse.data.forEach((address) => {
        const addressTypeString = addressTypeIdToString[address.addressTypeId]
        if (addressTypeString) {
          entityAddress[addressTypeString] = {
            countryCodeId: address.countryCodeId,
            countryCode: address.countryCode,
            countryText: address.countryText,
            addressLine1: address.addressLine1,
            addressLine2: address.addressLine2,
            city: address.city,
            stateProvince: address.stateProvince,
            postCode: address.postCode,
          }
        }
      })

      // Merge the entity contact and address data with the user data
      const userData = {
        ...userResponse.data,
        entityContact,
        entityAddress,
      }
      this.count = 1
      this.items = [new User(userData)]
      this.isLoading = false
      if (this.items.length === 1) {
        return this.items[0]
      } else {
        throw new Error('More than 1 found with id: ' + userDbId)
      }
    } catch (ex) {
      this.isLoading = false
      throw ex
    }
  }
  /**
   * /api/Users/querybysubdivision
   *
   * @param {number} subdivisionId
   * @returns {User[]} array of users
   *
   */
  async fetchBySubdivision(subdivisionId, page, pageSize, orderBy, filter) {
    this.isLoading = true
    try {
      const userResponse = await adminApiClient.getCompanyUsers(
        {
          subdivisionId: subdivisionId,
          Page: page,
          PageSize: pageSize,
          OrderBy: orderBy,
          Filter: filter,
        },
        subdivisionId,
      )

      this.count = userResponse.data.count
      this.items = userResponse.data.data.map((u) => new User(u))
      this.isLoading = false

      return this.items
    } catch (ex) {
      this.isLoading = false
      throw new Error(ex.message)
    }
  }

  /**
   * @override
   * DELETE /api/users
   *
   * @param {number} userDbId
   * @returns
   */
  async delete(userDbId) {
    let user = this.items.find((u) => u.userDbId === userDbId)
    if (user) {
      user.isLoading = true
    }

    try {
      // Get all entity contacts for the user
      const entityContactsResponse = await adminApiClient.getUsersEntityContacts(userDbId)
      // Delete all entity contacts
      await Promise.all(
        entityContactsResponse.data.map((contact) => adminApiClient.deleteEntityContact(contact.contactId)),
      )

      // Get all entity addresses for the user
      const entityAddressesResponse = await adminApiClient.getUsersEntityAddresses(userDbId)
      // Delete all entity addresses
      await Promise.all(
        entityAddressesResponse.data.map((address) => adminApiClient.deleteEntityAddress(address.addressId)),
      )
      // Delete the user
      const response = await adminApiClient.deleteUser(userDbId)

      if (user) {
        user.isLoading = false
      }

      if (response.status === 204) {
        let stillInArray = this.items.findIndex((u) => u.userDbId === userDbId)
        if (stillInArray > -1) {
          this.items.splice(stillInArray, 1)
        }
        this.removed = user
        return true
      }
      return false
    } catch (ex) {
      if (user) {
        user.isLoading = false
      }
      throw ex
    }
  }

  /**
   * @override
   * POST /api/users
   *
   * @param {User} user
   * @param {{ sendSetPasswordEmail?: boolean }} [options] when sendSetPasswordEmail is true the
   * user gets an email with a one-time link to set their own password instead of a password here
   */
  async create(user, options = {}) {
    user.authHash = undefined
    user.timestampUTC = undefined
    user.endpointId = undefined
    user.group = undefined
    user.company = undefined
    user.endpoint = undefined
    user.hasImage = undefined
    try {
      user.isLoading = true
      const response = await adminApiClient.createUser(user, options.sendSetPasswordEmail)
      if (response.status === 201) {
        user.userDbId = response.data
        // Entity Contact type mapping
        const contactTypes = {
          phone: { id: 1, value: user.entityContact?.phone },
          commercialPhone: { id: 2, value: user.entityContact?.commercialPhone },
          email: { id: 3, value: user.entityContact?.email },
          invoicingEmail: { id: 4, value: user.entityContact?.invoicingEmail },
          trainingEmail: { id: 5, value: user.entityContact?.trainingEmail },
        }

        // Helper function to create contact
        const createContact = async (contactTypeId, contactValue) => {
          if (contactValue) {
            const contactData = {
              contactTypeId,
              entityTypeId: 3,
              entityId: user.userDbId,
              contact: contactValue,
            }
            await adminApiClient.createEntityContact(contactData)
          }
        }

        // Create all contacts
        await Promise.all(Object.values(contactTypes).map(({ id, value }) => createContact(id, value)))

        // Create entity addresses
        if (user.entityAddress) {
          // Address type mapping
          const addressTypeMap = {
            primaryAddress: 1,
            secondaryAddress: 2,
            invoicingAddress: 3,
          }

          for (const [addressTypeId, address] of Object.entries(user.entityAddress)) {
            const addressData = {
              entityTypeId: 3,
              entityId: user.userDbId,
              addressTypeId: addressTypeMap[addressTypeId],
              ...address,
            }
            await adminApiClient.createEntityAddress(addressData)
          }
        }

        user.isLoading = false
        this.items.push(user)
        this.added = user
        return user
      }
      throw new Error('Failed to create user!')
    } catch (ex) {
      user.isLoading = false
      this.isLoading = false
      throw ex
    }
  }

  /**
   * Connects user to subdivision
   *
   * POST /api/Users/addorupdatesubdivision
   *
   * @param {*} connectionProps
   */
  async connectUserToSubdivision(connectionProps) {
    const params = {
      userId: '' + connectionProps.user.userDbId,
      companyId: connectionProps.companyId,
      groupId: null,
      externalCompanyUser: connectionProps.externalCompanyUser,
      endpointId: connectionProps.endpointId !== 0 ? connectionProps.endpointId : null,
      subdivisionDefinition: {
        subdivisionId: connectionProps.subdivisionId,
        externalCompanyNumber: null,
        externalCompanyId: null,
      },
    }
    connectionProps.user.isLoading = true
    this.isLoading = true
    try {
      const response = await adminApiClient.connectUserToSubdivision(params)
      if (response.status === 200) {
        const index = this.items.findIndex((item) => item.userDbId == connectionProps.user.userDbId)
        if (index > -1) {
          this.updateItem(connectionProps.user, this.collectionKey)
          this.updated = connectionProps.user
        } else {
          this.items.push(connectionProps.user)
        }
        this.added = connectionProps.user
        return true
      } else {
        throw new Error('Failed to add connection')
      }
    } catch (ex) {
      this.isLoading = false
      connectionProps.user.isLoading = false
      throw ex
    }
  }
  /**
   * removes user to subdivsion connection
   *
   * POST /api/Users/removesubdivision
   * @param {*} userId
   * @param {*} subdivisionId
   * @returns
   */
  removeUserSubdivisionConnection(userDbId, companyId, subdivisionId) {
    let user = this.items.find((u) => u.userDbId === userDbId)
    if (user) {
      user.isLoading = true
    }
    return adminApiClient
      .removeUserFromSubdivision({
        userId: userDbId,
        companyId: companyId,
        SubdivisionId: subdivisionId,
        externalCompanyNumber: null,
      })
      .then((response) => {
        if (user) {
          user.isLoading = false
        }
        if (response.status === 204) {
          let stillInArray = this.items.findIndex((u) => u.userDbId === userDbId)
          if (stillInArray > -1) {
            this.items.splice(stillInArray, 1)
          }
          this.removed = user
          return true
        }
        throw new Error('Failed to remove')
      })
      .catch((ex) => {
        if (user) {
          user.isLoading = false
        }
        throw ex
      })
  }

  async queryAll(s) {
    this.isLoading = true
    try {
      const userResponse = await adminApiClient.getUsers({
        Page: 0,
        PageSize: 42,
        Filter: 'userId=*' + s + '/i|userName=*' + s + '/i',
        orderBy: 'userId asc',
      })
      this.count = userResponse.data.count
      this.items = userResponse.data.data
      this.isLoading = false

      return this.items
    } catch (ex) {
      this.isLoading = false
      throw new Error(ex.message)
    }
  }
}
